/**
 * The tab title, the meta description and (for an unpublished page) the
 * robots rule for one public page. Every page under routes/_site calls it
 * once, near the top:
 *
 *   useDocumentMeta({ title: 'Features', description: 'What Pleach does, screen by screen.' });
 *   useDocumentMeta({ title: 'Pricing', description: '...', noindex: true }); // OD-B2
 *   useDocumentMeta({ description: '...' });  // the home page: "Pleach · Kitchen costing ..."
 *
 * The title reads "Features · Pleach", the way the app titles its sections.
 * When the page goes away the previous values come back, so a page outside
 * the site (login, a gate, the app) never inherits a site page's title,
 * description or noindex. A layout effect, so the new title is in place
 * before the page paints.
 *
 * This runs in the browser only. Crawlers that do not run scripts, and
 * social previews, need the same values in the HTML itself; that is the
 * server-side meta for public paths in PLAN section 11 (P2, W4-6).
 */
import { useLayoutEffect } from 'react';

export const SITE_NAME = 'Pleach';

/** The home page's title, and the title of any page that gives none. */
export const SITE_HOME_TITLE = `${SITE_NAME} · Kitchen costing for professional kitchens`;

export type DocumentMeta = {
  /** The page's own name, e.g. "Help centre". Leave it out on the home page. */
  title?: string;
  /** One or two plain sentences for search results, about 150 characters. */
  description: string;
  /** Keep the page out of search engines (OD-B2: Pricing until it is
   *  published). Adds <meta name="robots" content="noindex">. */
  noindex?: boolean;
};

/** "Help centre" -> "Help centre · Pleach"; nothing -> the home title. */
export function documentTitle(title?: string): string {
  const t = title?.trim();
  return t ? `${t} · ${SITE_NAME}` : SITE_HOME_TITLE;
}

function descriptionTag(): { el: HTMLMetaElement; created: boolean } {
  const found = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (found) return { el: found, created: false };
  const el = document.createElement('meta');
  el.name = 'description';
  document.head.appendChild(el);
  return { el, created: true };
}

export function useDocumentMeta({ title, description, noindex = false }: DocumentMeta): void {
  useLayoutEffect(() => {
    const fullTitle = documentTitle(title);
    const prevTitle = document.title;
    document.title = fullTitle;

    const { el: desc, created } = descriptionTag();
    const prevDesc = desc.getAttribute('content');
    desc.setAttribute('content', description);

    let robots: HTMLMetaElement | null = null;
    if (noindex) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      robots.content = 'noindex';
      robots.setAttribute('data-site-meta', '');
      document.head.appendChild(robots);
    }

    return () => {
      // Put back what was there, unless something newer has already
      // written its own value.
      if (document.title === fullTitle) document.title = prevTitle;
      if (desc.getAttribute('content') === description) {
        if (created) desc.remove();
        else if (prevDesc === null) desc.removeAttribute('content');
        else desc.setAttribute('content', prevDesc);
      }
      robots?.remove();
    };
  }, [title, description, noindex]);
}
